import { Injectable, PLATFORM_ID, inject, signal, computed } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import type {
  AutoMicGainState,
  AutoSyncState,
  ClickVolume,
  ControlLocks,
  FillLightConfig,
  FilterKey,
  FocusTarget,
  FrameKey,
  Mode,
  PanelKind,
  RecState,
  Stage,
  SyncSource,
} from './recorder.types';

/**
 * RecorderStore — signal-based feature store for the SingSnap recorder.
 *
 * Owns all persistent state and side effects (elapsed timer, persistence,
 * focus targets, announcements). Components remain Input/Output-only.
 *
 * Persistence keys (SSR-safe, write-on-change, hydrate-on-bootstrap):
 *   ss.devices, ss.lighting, ss.audio, ss.performance, ss.sync
 */
@Injectable({ providedIn: 'root' })
export class RecorderStore {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  // -------- core state --------
  readonly recState = signal<RecState>('idle');

  /**
   * Active workflow. `'recording'` is the canonical capture flow.
   * `'practice'` is a rehearsal flow that never produces a recording, has no
   * Playback / Review / Save stage, and unlocks Camera / Mic / Auto Mic Gain
   * / Pitch / Volume during the run.
   */
  readonly mode = signal<Mode>('recording');

  readonly camera = signal<string>('');
  readonly mic = signal<string>('');

  readonly fullscreen = signal<boolean>(false);
  readonly fsPanel = signal<PanelKind | null>(null);

  /** Live-region announcement consumed by RecorderShellComponent. */
  readonly announce = signal<string>('');

  /** Focus target consumed by FooterControlsComponent / FullscreenOverlayComponent. */
  readonly focusTarget = signal<FocusTarget>(null);

  // -------- workflow stage --------
  readonly stage = computed<Stage>(() => {
    switch (this.recState()) {
      case 'recording':
        return 'record';
      case 'review':
        return 'review';
      case 'practicing':
        return 'practice';
      default:
        return 'setup';
    }
  });

  // -------- auto mic gain (ss.audio) --------
  readonly autoMicGain = signal<AutoMicGainState>('off');
  readonly autoMicGainEnabled = computed(() => this.autoMicGain() !== 'off');
  /** Calibration progress 0–1; meaningful while state === 'calculating'. */
  readonly autoMicGainProgress = signal<number>(0);
  /** Best Level (signed dB) after calibration completes. Null when off. */
  readonly bestLevelDb = signal<number | null>(null);

  // -------- screen fill light (ss.lighting) --------
  readonly fillLight = signal<FillLightConfig>({
    on: true,
    brightness: 0,
    toneOn: false,
    warmth: 50,
  });

  // -------- performance (ss.performance) --------
  readonly volume = signal<number>(0);     // -12..+12 semitone-ish step
  readonly pitch = signal<number>(0);      // -12..+12
  readonly lyricSize = signal<number>(3);  // 1..5
  readonly filter = signal<FilterKey>('none');
  readonly frame = signal<FrameKey>('none');

  // -------- mix --------
  readonly micVolume = signal<number>(0);   // -12..+12 dB
  readonly musicVolume = signal<number>(0); // -12..+12 dB

  // -------- sync (ss.sync) --------
  readonly sync = signal<number>(0);              // ms
  readonly syncSource = signal<SyncSource>('manual');
  readonly boostMic = signal<boolean>(false);
  readonly clickVolume = signal<ClickVolume>('normal');

  // -------- auto sync workflow (transient) --------
  readonly autoSyncState = signal<AutoSyncState>('initial');
  readonly autoSyncMeasurements = signal<number[]>([]);
  readonly recommendedSync = signal<number | null>(null);

  // -------- recording timing --------
  readonly countdown = signal<number | null>(null); // 3..2..1..null
  /** Live elapsed milliseconds while in 'recording'. Driven by internal timer. */
  readonly elapsed = signal<number>(0);

  private elapsedTimerId: ReturnType<typeof setInterval> | null = null;
  private elapsedStart = 0;

  /** Live elapsed milliseconds while in 'practicing'. */
  readonly practiceElapsed = signal<number>(0);

  /** Current backing-track position (ms). Transport-only — never persisted. */
  readonly songPositionMs = signal<number>(0);

  private practiceTimerId: ReturnType<typeof setInterval> | null = null;
  private practiceStart = 0;

  // -------- derived --------
  readonly audioOnly = computed(() => {
    const cam = this.camera().trim().toLowerCase();
    return cam === '' || cam === 'camera off';
  });

  readonly locked = computed(() => this.recState() === 'recording');

  readonly setupComplete = computed(
    () => this.camera().trim() !== '' && this.mic().trim() !== ''
  );

  /**
   * Per-mode control locks. Recording locks Camera / Mic / AMG / Pitch.
   * Practice unlocks everything so the user can rehearse and tune settings
   * before recording. Idle / review / practice-stopped never lock.
   */
  readonly controlLocks = computed<ControlLocks>(() => {
    const s = this.recState();
    if (s === 'recording') {
      return { camera: true, mic: true, autoMicGain: true, pitch: true, volume: false };
    }
    // Practicing and every non-recording state leave all controls adjustable.
    return { camera: false, mic: false, autoMicGain: false, pitch: false, volume: false };
  });

  // -------- device mutations --------
  setCamera(value: string): void {
    this.camera.set(value);
    this.persistDevices();
  }

  setMic(value: string): void {
    this.mic.set(value);
    this.persistDevices();
  }

  // -------- recState + timer + focus --------
  setRecState(next: RecState): void {
    const prev = this.recState();
    if (prev === next) return;
    this.recState.set(next);

    switch (next) {
      case 'idle':
        this.mode.set('recording');
        this.stopElapsedTimer();
        this.stopPracticeTimer();
        this.elapsed.set(0);
        this.practiceElapsed.set(0);
        this.announce.set('Ready to record.');
        this.focusTarget.set(this.setupComplete() ? 'record' : null);
        break;
      case 'recording':
        this.mode.set('recording');
        this.stopPracticeTimer();
        this.startElapsedTimer();
        this.announce.set('Recording started.');
        this.focusTarget.set('stop');
        break;
      case 'review':
        this.mode.set('recording');
        this.stopElapsedTimer();
        this.stopPracticeTimer();
        this.announce.set('Recording stopped. Review opened.');
        this.focusTarget.set('save');
        // Stop-in-fullscreen: auto-open Playback / Review without exiting
        // fullscreen. Only fires for the Recording flow — Practice Stop must
        // never open Review.
        if (this.fullscreen() && this.mode() === 'recording') {
          this.fsPanel.set('playback-review');
        }
        break;
      case 'practicing':
        this.mode.set('practice');
        this.stopElapsedTimer();
        this.startPracticeTimer();
        this.announce.set('Practicing.');
        this.focusTarget.set('stop-practice');
        break;
      case 'practice-stopped':
        this.stopPracticeTimer();
        this.announce.set('Practice stopped.');
        this.focusTarget.set('record-from-practice');
        break;
    }
  }

  // -------- practice mutations (transport-only — never persisted) --------

  /**
   * Begin a rehearsal. Switches mode to `'practice'`, sets recState to
   * `'practicing'`, and resets song position to 0. All recorder settings
   * (devices / AMG / fill light / filters / frames / volume / pitch / sync)
   * are preserved.
   */
  startPractice(): void {
    if (!this.setupComplete()) return;
    this.mode.set('practice');
    this.songPositionMs.set(0);
    this.practiceElapsed.set(0);
    this.setRecState('practicing');
  }

  /**
   * Stop the rehearsal. Transitions to `'practice-stopped'` so the user can
   * choose Restart or Record. NEVER opens Playback / Review.
   */
  stopPractice(): void {
    if (this.recState() !== 'practicing') return;
    this.setRecState('practice-stopped');
  }

  /**
   * Restart — transport-only reset. Returns song position to 0:00 and the
   * recorder to a ready Idle state with `mode = 'recording'` so the standard
   * Record + Practice buttons reappear. All recorder preferences persist.
   */
  restartPractice(): void {
    this.songPositionMs.set(0);
    this.practiceElapsed.set(0);
    this.mode.set('recording');
    this.setRecState('idle');
  }

  /**
   * Begin a Recording from a stopped-practice state. Returns song position
   * to 0:00, flips mode back to Recording, and enters the standard Recording
   * flow immediately. All recorder preferences persist.
   */
  recordFromPractice(): void {
    this.songPositionMs.set(0);
    this.mode.set('recording');
    this.setRecState('recording');
  }


  toggleFullscreen(): void {
    const next = !this.fullscreen();
    this.fullscreen.set(next);
    if (!next) {
      this.fsPanel.set(null);
    }
  }

  setFsPanel(panel: PanelKind | null): void {
    this.fsPanel.set(panel);
  }

  clearAnnouncement(): void {
    this.announce.set('');
  }

  // -------- auto mic gain mutations (spec §8) --------

  /** Turn AMG on — always forces a fresh calibration. */
  enableAutoMicGain(): void {
    this.bestLevelDb.set(null);
    this.autoMicGainProgress.set(0);
    this.autoMicGain.set('calculating');
    this.persistAudio();
  }

  /** Turn AMG off — clears Best Level + resets calibration state. */
  disableAutoMicGain(): void {
    this.autoMicGain.set('off');
    this.autoMicGainProgress.set(0);
    this.bestLevelDb.set(null);
    this.persistAudio();
  }

  /** Re-enter calculating from a calibrated state. */
  recalibrateAutoMicGain(): void {
    this.bestLevelDb.set(null);
    this.autoMicGainProgress.set(0);
    this.autoMicGain.set('calculating');
    this.persistAudio();
  }

  setAutoMicGainProgress(p: number): void {
    this.autoMicGainProgress.set(Math.max(0, Math.min(1, p)));
  }

  /** Called by the calibration runner with the resolved signed-dB result. */
  completeAutoMicGain(bestLevelDb: number): void {
    this.bestLevelDb.set(Math.round(bestLevelDb));
    this.autoMicGain.set('complete');
    this.persistAudio();
  }

  /** Confirm transition complete → calibrated (called after the success flash). */
  settleAutoMicGain(): void {
    if (this.autoMicGain() === 'complete') {
      this.autoMicGain.set('calibrated');
      this.persistAudio();
    }
  }

  // -------- screen fill light mutations (spec §9) --------

  setBrightness(brightness: number): void {
    const clamped = Math.max(0, Math.min(100, Math.round(brightness)));
    this.fillLight.update((cur) => ({ ...cur, brightness: clamped }));
    this.persistLighting();
  }

  setToneOn(on: boolean): void {
    this.fillLight.update((cur) => ({ ...cur, toneOn: on }));
    this.persistLighting();
  }

  setWarmth(warmth: number): void {
    const clamped = Math.max(0, Math.min(100, Math.round(warmth)));
    this.fillLight.update((cur) => ({ ...cur, warmth: clamped }));
    this.persistLighting();
  }

  // -------- performance mutations (ss.performance) --------
  setVolume(v: number): void { this.volume.set(v); this.persistPerformance(); }
  setPitch(v: number): void { this.pitch.set(v); this.persistPerformance(); }
  setLyricSize(v: number): void { this.lyricSize.set(v); this.persistPerformance(); }
  setFilter(key: FilterKey): void { this.filter.set(key); this.persistPerformance(); }
  setFrame(key: FrameKey): void { this.frame.set(key); this.persistPerformance(); }

  // -------- mix --------
  setMicVolume(v: number): void { this.micVolume.set(v); }
  setMusicVolume(v: number): void { this.musicVolume.set(v); }

  // -------- sync mutations (ss.sync) --------
  setSync(value: number): void {
    this.sync.set(Math.round(value));
    this.syncSource.set('manual');
    this.persistSync();
  }

  applyAutoSync(value: number): void {
    this.sync.set(Math.round(value));
    this.syncSource.set('auto');
    this.persistSync();
  }

  resetSync(): void {
    this.sync.set(0);
    this.syncSource.set('manual');
    this.persistSync();
  }

  setBoostMic(on: boolean): void {
    this.boostMic.set(on);
    this.persistSync();
  }

  setClickVolume(v: ClickVolume): void {
    this.clickVolume.set(v);
    this.persistSync();
  }

  // -------- auto sync workflow (transient) --------
  setAutoSyncState(state: AutoSyncState): void { this.autoSyncState.set(state); }
  pushAutoSyncMeasurement(ms: number): void {
    this.autoSyncMeasurements.update((arr) => [...arr, Math.round(ms)]);
  }
  resetAutoSyncWorkflow(): void {
    this.autoSyncMeasurements.set([]);
    this.recommendedSync.set(null);
    this.autoSyncState.set('initial');
  }
  setRecommendedSync(ms: number | null): void { this.recommendedSync.set(ms); }

  // -------- countdown --------
  setCountdown(n: number | null): void { this.countdown.set(n); }

  // -------- elapsed timer --------
  private startElapsedTimer(): void {
    this.stopElapsedTimer();
    if (!this.isBrowser) return;
    this.elapsedStart = Date.now();
    this.elapsed.set(0);
    this.elapsedTimerId = setInterval(() => {
      this.elapsed.set(Date.now() - this.elapsedStart);
    }, 200);
  }

  private stopElapsedTimer(): void {
    if (this.elapsedTimerId !== null) {
      clearInterval(this.elapsedTimerId);
      this.elapsedTimerId = null;
    }
  }

  // -------- practice timer --------
  private startPracticeTimer(): void {
    this.stopPracticeTimer();
    if (!this.isBrowser) return;
    this.practiceStart = Date.now();
    this.practiceElapsed.set(0);
    this.practiceTimerId = setInterval(() => {
      this.practiceElapsed.set(Date.now() - this.practiceStart);
    }, 200);
  }

  private stopPracticeTimer(): void {
    if (this.practiceTimerId !== null) {
      clearInterval(this.practiceTimerId);
      this.practiceTimerId = null;
    }
  }

  // -------- persistence (SSR-safe) --------
  private write(key: string, value: unknown): void {
    if (!this.isBrowser) return;
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* non-fatal */
    }
  }

  private persistDevices(): void {
    this.write('ss.devices', { camera: this.camera(), mic: this.mic() });
  }

  private persistLighting(): void {
    const fl = this.fillLight();
    this.write('ss.lighting', {
      fillLight: fl.on,
      brightness: fl.brightness,
      toneOn: fl.toneOn,
      warmth: fl.warmth,
    });
  }

  private persistAudio(): void {
    this.write('ss.audio', {
      autoMicGain: this.autoMicGainEnabled(),
      bestLevelDb: this.bestLevelDb(),
      autoMicGainCalibrationState: this.autoMicGain(),
    });
  }

  private persistSync(): void {
    this.write('ss.sync', {
      value: this.sync(),
      source: this.syncSource(),
      boostMic: this.boostMic(),
      clickVolume: this.clickVolume(),
    });
  }

  private persistPerformance(): void {
    this.write('ss.performance', {
      volume: this.volume(),
      pitch: this.pitch(),
      lyricSize: this.lyricSize(),
      filter: this.filter(),
      frame: this.frame(),
    });
  }

  /** Call once during app bootstrap (e.g. APP_INITIALIZER). */
  hydrate(): void {
    if (!this.isBrowser) return;
    try {
      const dev = localStorage.getItem('ss.devices');
      if (dev) {
        const p = JSON.parse(dev) as { camera?: string; mic?: string };
        if (typeof p.camera === 'string') this.camera.set(p.camera);
        if (typeof p.mic === 'string') this.mic.set(p.mic);
      }

      const lighting = localStorage.getItem('ss.lighting');
      if (lighting) {
        const p = JSON.parse(lighting) as {
          fillLight?: boolean;
          brightness?: number;
          toneOn?: boolean;
          warmth?: number;
        };
        // Migration: in the old design `fillLight === false` meant "off".
        // The new design has no on/off — emulate "off" with brightness=0 +
        // tone off so legacy users see no visible fill light on first load.
        const legacyOff = p.fillLight === false;
        this.fillLight.update((cur) => ({
          on: true,
          brightness:
            legacyOff ? 0
              : typeof p.brightness === 'number' ? p.brightness : cur.brightness,
          toneOn: legacyOff ? false
            : typeof p.toneOn === 'boolean' ? p.toneOn : cur.toneOn,
          warmth: typeof p.warmth === 'number' ? p.warmth : cur.warmth,
        }));
      }

      const audio = localStorage.getItem('ss.audio');
      if (audio) {
        const p = JSON.parse(audio) as {
          autoMicGain?: boolean;
          bestLevelDb?: number | null;
          autoMicGainCalibrationState?: AutoMicGainState;
        };
        if (p.autoMicGainCalibrationState) {
          this.autoMicGain.set(p.autoMicGainCalibrationState);
        } else if (p.autoMicGain) {
          this.autoMicGain.set('calibrated');
        }
        if (typeof p.bestLevelDb === 'number') {
          this.bestLevelDb.set(p.bestLevelDb);
        }
      }

      const perf = localStorage.getItem('ss.performance');
      if (perf) {
        const p = JSON.parse(perf) as {
          volume?: number;
          pitch?: number;
          lyricSize?: number;
          filter?: string;
          frame?: string;
        };
        if (typeof p.volume === 'number') this.volume.set(p.volume);
        if (typeof p.pitch === 'number') this.pitch.set(p.pitch);
        if (typeof p.lyricSize === 'number') this.lyricSize.set(p.lyricSize);
        if (typeof p.filter === 'string') this.filter.set(p.filter);
        if (typeof p.frame === 'string') this.frame.set(p.frame);
      }

      const sync = localStorage.getItem('ss.sync');
      if (sync) {
        const p = JSON.parse(sync) as {
          value?: number;
          source?: SyncSource;
          boostMic?: boolean;
          clickVolume?: ClickVolume;
        };
        if (typeof p.value === 'number') this.sync.set(p.value);
        if (p.source === 'manual' || p.source === 'auto') {
          this.syncSource.set(p.source);
        }
        if (typeof p.boostMic === 'boolean') this.boostMic.set(p.boostMic);
        if (p.clickVolume) this.clickVolume.set(p.clickVolume);
      }
    } catch {
      /* malformed — ignore */
    }
  }
}
